import { useEffect, useState } from "react";
import { Edit, Trash2, PlusCircle, ChevronRight, ChevronLeft, Check } from "lucide-react";
import toast from "react-hot-toast";
import axiosInstance from "../../api/axios";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../../components/confirm-modal/ConfirmModal";

export default function Products() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [page, setPage] = useState(1);
    const [limit] = useState(20);
    const [selected, setSelected] = useState([]);
    const [showConfirm, setShowConfirm] = useState(false);
    const [type, setType] = useState("product");
    const [pagination, setPagination] = useState({
        hasNextPage: false,
        hasPrevPage: false,
        total: 0,
    });

    const fetchProducts = async (pageNumber = 1, productType = type) => {
        setLoading(true);
        try {
            const { data } = await axiosInstance.get("/api/product/get-list", {
                params: {
                    isPagination: true,
                    isEnglish: false,
                    type: productType,
                    page: pageNumber,
                    limit,
                },
            });

            setProducts(data.data?.docs || []);
            setPagination({
                hasNextPage: data.data?.hasNextPage,
                hasPrevPage: data.data?.hasPrevPage,
                total: data.data?.total || 0,
            });
        } catch {
            toast.error("خطا در دریافت لیست محصولات");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProducts(page, type);
        setSelected([]);
    }, [page, type]);

    const handlePrev = () => pagination.hasPrevPage && setPage((p) => p - 1);
    const handleNext = () => pagination.hasNextPage && setPage((p) => p + 1);

    const toggleSelect = (id) => {
        setSelected((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleDeleteSelected = () => {
        if (selected.length === 0) {
            toast.error("هیچ محصولی انتخاب نشده است");
            return;
        }
        setShowConfirm(true);
    };

    const confirmDelete = async () => {
        setDeleting(true);
        try {
            await axiosInstance.delete("/api/product/delete-list", {
                data: { product_ids: selected },
            });
            toast.success("محصولات با موفقیت حذف شدند");
            setSelected([]);
            setShowConfirm(false);
            fetchProducts(page, type);
        } catch {
            toast.error("خطا در حذف محصولات");
        } finally {
            setDeleting(false);
        }
    };

    return (
        <section className="w-full max-w-4xl mx-auto flex flex-col gap-6">
            <div className="flex flex-wrap justify-between items-center gap-3 py-3">
                <h2 className="text-lg md:text-xl font-semibold text-background">
                    لیست محصولات
                </h2>

                <div className="flex flex-wrap gap-2">
                    {selected.length > 0 && (
                        <button
                            onClick={handleDeleteSelected}
                            className="flex items-center text-sm gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                            <Trash2 size={18} />
                            حذف {selected.length} محصول
                        </button>
                    )}
                    <button
                        className="flex items-center text-sm gap-2 bg-accent hover:bg-accent/80 transition-colors text-foreground px-4 py-2 rounded-lg font-medium"
                        onClick={() => navigate('/create-product')}
                    >
                        <PlusCircle size={18} />
                        ایجاد محصول جدید
                    </button>
                </div>
            </div>

            <div>

                <div className="tablet:mr-4 flex justify-center tablet:justify-start">
                    {[
                        { key: "product", label: "ماشین‌آلات" },
                        { key: "service", label: "خدمات" },
                    ].map((tab) => (
                        <button
                            key={tab.key}
                            onClick={() => {
                                setType(tab.key);
                                setPage(1);
                            }}
                            className={`w-24 tablet:w-28 py-1 text-sm md:text-base border font-medium transition-colors ${type === tab.key
                                ? "bg-accent text-foreground border-accent border-b-transparent"
                                : "text-black/60 hover:text-background border-black/20 border-b-transparent"
                                }
                                ${tab.key === "product"
                                    ? "rounded-tr-lg"
                                    : "rounded-tl-lg"
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="overflow-x-auto border border-black/10 rounded-xl">
                    <table className="min-w-full text-sm text-right text-background">
                        <thead className="bg-black/5 border-b border-black/10 text-black/70">
                            <tr>
                                <th className="px-4 py-3 text-center">انتخاب</th>
                                <th className="px-4 py-3">تصویر</th>
                                <th className="px-4 py-3">عنوان</th>
                                <th className="px-4 py-3 hidden md:table-cell">توضیحات</th>
                                <th className="px-4 py-3 text-center">ویرایش</th>
                            </tr>
                        </thead>

                        {loading ? (
                            <tbody>
                                {Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={i} className="border-b border-black/10 animate-pulse">
                                        <td className="px-4 py-3">
                                            <div className="w-5 h-5 bg-black/10 rounded-full mx-auto" />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="w-14 h-14 bg-black/10 rounded-lg" />
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="h-4 w-32 bg-black/10 rounded"></div>
                                        </td>
                                        <td className="px-4 py-3 hidden md:table-cell">
                                            <div className="h-4 w-64 bg-black/10 rounded"></div>
                                        </td>
                                        <td className="px-4 py-3 flex justify-center gap-2">
                                            <div className="w-6 h-6 bg-black/10 rounded" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        ) : (
                            <tbody>
                                {products.length === 0 ? (
                                    <tr>
                                        <td colSpan={5} className="text-center text-background py-10">
                                            هیچ موردی یافت نشد
                                        </td>
                                    </tr>
                                ) : (
                                    products.map((p, index) => (
                                        <tr
                                            key={p.id || index}
                                            className={`border-b border-black/10 hover:bg-black/5 transition-colors ${index === products.length - 1 && "border-transparent"
                                                }`}
                                        >
                                            <td className="px-4 py-3 text-center flex justify-center items-center">
                                                <button
                                                    onClick={() => toggleSelect(p.id)}
                                                    className={`size-5 rounded-full border transition-colors ${selected.includes(p.id)
                                                        ? "bg-accent border-accent"
                                                        : "border-black/30"
                                                        }`}
                                                >
                                                    <Check
                                                        className={`size-full text-foreground p-0.5 ${selected.includes(p.id)
                                                            ? "opacity-100"
                                                            : "opacity-0"
                                                            }`}
                                                    />
                                                </button>
                                            </td>
                                            <td className="px-4 py-3">
                                                <img
                                                    src={
                                                        (p.image &&
                                                            `${process.env.REACT_APP_BASE_URL}/${p.image}`) ||
                                                        "/no-image.png"
                                                    }
                                                    alt={p.title}
                                                    className="size-14 object-cover rounded-lg border border-black/10"
                                                />
                                            </td>
                                            <td className="px-4 py-3 truncate max-w-[100px] md:max-w-[180px]">
                                                {p.title}
                                            </td>
                                            <td className="px-4 py-3 hidden md:table-cell text-black/70 truncate max-w-[250px]">
                                                {p.description}
                                            </td>
                                            <td className="px-4 py-3 flex justify-center items-center gap-3">
                                                <button
                                                    onClick={() => navigate(`/edit-product/${p.id}`)}
                                                    className="text-black/70 p-2 hover:bg-black/10 rounded-lg transition-colors"
                                                >
                                                    <Edit size={20} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        )}
                    </table>
                </div>
            </div>

            {!loading && (
                <div className="flex justify-center items-center gap-4 mt-4 flex-wrap">
                    <button
                        onClick={handlePrev}
                        disabled={!pagination.hasPrevPage}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border border-black/20 transition-colors ${pagination.hasPrevPage
                            ? "hover:bg-black/10 text-background"
                            : "text-black/40 cursor-not-allowed"
                            }`}
                    >
                        <ChevronRight size={16} />
                        قبلی
                    </button>

                    <span className="text-black/70 text-sm">
                        صفحه {page} از {Math.ceil(pagination.total / limit) || 1}
                    </span>

                    <button
                        onClick={handleNext}
                        disabled={!pagination.hasNextPage}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border border-black/20 transition-colors ${pagination.hasNextPage
                            ? "hover:bg-black/10 text-background"
                            : "text-black/40 cursor-not-allowed"
                            }`}
                    >
                        بعدی
                        <ChevronLeft size={16} />
                    </button>
                </div>
            )}

            <ConfirmModal
                isOpen={showConfirm}
                onClose={() => !deleting && setShowConfirm(false)}
                onConfirm={confirmDelete}
                title={"حذف محصولات"}
                description={`آیا از حذف ${selected.length} محصول انتخاب‌شده اطمینان دارید؟`}
                confirmText="بله، حذف شود"
                cancelText="انصراف"
                loading={deleting}
            />
        </section>
    );
}